import { useQueries } from 'react-query';
import { searchCategoryNames } from '../api/cmsApi';
import { CMSSearchResponse } from '../types/CMSTypes';

interface TileWithApplication {
  application?: string | null;
  item_group?: string | null;
  // parent_category_id -> image url, set per collection in the hub
  custom_tile_data_urls?: Record<string, string> | null;
}

const normalise = (value?: string | null): string => (value || '').trim().toLowerCase();

// Key for looking up an application's category id from a tile's application name + item group
export const applicationKey = (application?: string | null, itemGroup?: string | null): string =>
  `${normalise(application)}|${itemGroup || ''}`;

/**
 * Custom pages bucket collections by application NAME, but a collection's per-parent tile images
 * (custom_tile_data_urls) are keyed by the application's category id. This resolves
 * (application name + item group) -> category id, only for tiles that actually have per-parent
 * images, so pages that don't use the feature make no extra requests.
 */
export const useApplicationCategoryIds = (tiles: TileWithApplication[]): Map<string, string> => {
  const namesToSearch = new Map<string, string>();
  tiles.forEach((tile) => {
    if (!tile.application || Object.keys(tile.custom_tile_data_urls || {}).length === 0) return;
    namesToSearch.set(normalise(tile.application), tile.application);
  });
  const searchTerms = Array.from(namesToSearch.values());

  const results = useQueries(
    searchTerms.map((name) => ({
      queryKey: ['application-category-ids', normalise(name)],
      queryFn: () => searchCategoryNames(name),
      staleTime: 600000,
      retry: false,
    }))
  );

  const ids = new Map<string, string>();
  results.forEach((result, index) => {
    const wanted = normalise(searchTerms[index]);
    const applications = (result.data as CMSSearchResponse | undefined)?.results?.application || [];
    applications.forEach((app) => {
      // An application category's `application` can be a pipe-delimited list (e.g.
      // "Upholstery|Curtaining"), so its category_name is the reliable name to match on.
      const isMatch = normalise(app.category_name) === wanted || normalise(app.application) === wanted;
      const key = applicationKey(wanted, app.item_group);
      if (isMatch && !ids.has(key)) {
        ids.set(key, app.id);
      }
    });
  });
  return ids;
};
